import { AbilitiesByLevelCollection } from "../../abilities/AbilitiesByLevelCollection";
import { CharacterHelpers, CharacterInitializer } from "../../characters/Character";
import { CreatureType } from "../../characters/CreatureType";
import { OwnedMinionReference } from "../../Ownership";

export type MinionInitializer = {
    id: string;
    name: string;
    description: string;
    creatureTypes: CreatureType[];
    baseHealth: number;
    healthScale: number;
    armor: number;
    resilience: number;
    featuresByLevel: AbilitiesByLevelCollection[];
}

export const DefaultMinionInitializer: MinionInitializer = {
    id: '',
    name: '',
    description: '',
    creatureTypes: [],
    baseHealth: 1,
    healthScale: 1,
    armor: 0,
    resilience: 0,
    featuresByLevel: [],
}

const getMaxHealth = (minion: MinionInitializer, character: CharacterInitializer) => {
    const reference = character.minions.find(m => m.id == minion.id);
    if (reference == null) {
        throw "Minion not found.";
    }
    const commandSkillLevel = CharacterHelpers.getSkillLevel(character, 'Command');
    return minion.baseHealth + minion.healthScale * commandSkillLevel;
}

const getDisplayName = (minion: MinionInitializer, reference: OwnedMinionReference) => {
    return reference.customName ?? minion.name;
}

const getAcquiredFeatures = (level: number, minion: MinionInitializer) => {
    return minion.featuresByLevel.filter(fbl => fbl.level <= level);
}

const getActionsForLevel = (minion: MinionInitializer, character: CharacterInitializer) => {
    const commandSkillLevel = CharacterHelpers.getSkillLevel(character, 'Command');
    return getAcquiredFeatures(commandSkillLevel, minion)
        .mapMany(sf => sf.activeAbilities.map(aa => ({ ...aa, source: `${minion.name} ${sf.level}` })));
}

const getReactionsForLevel = (minion: MinionInitializer, character: CharacterInitializer) => {
    const commandSkillLevel = CharacterHelpers.getSkillLevel(character, 'Command');
    return getAcquiredFeatures(commandSkillLevel, minion)
        .mapMany(sf => sf.reactiveAbilities.map(aa => ({ ...aa, source: `${minion.name} ${sf.level}` })));
}

const getPassivesForLevel = (minion: MinionInitializer, character: CharacterInitializer) => {
    const commandSkillLevel = CharacterHelpers.getSkillLevel(character, 'Command');
    return getAcquiredFeatures(commandSkillLevel, minion)
        .mapMany(sf => sf.passiveAbilities.map(aa => ({ ...aa, source: `${minion.name} ${sf.level}` })));
}

export const MinionHelpers = Object.freeze({
    getMaxHealth,
    getDisplayName,
    getActionsForLevel,
    getReactionsForLevel,
    getPassivesForLevel,
})